/**
 * Shared row components for the job form.
 *
 * These were previously defined twice - once in JobBuilderPage and again in
 * JobEditPage - and the two copies had drifted apart in seven of ten cases.
 * They now live here so a change reaches both creating and editing a job.
 *
 * All components are presentational: they take props and call back, and hold
 * no page state. Where creating and editing genuinely need to differ, that is
 * an explicit prop (see SortablePhaseItem's preserveCustomRate), never a
 * second copy of the component.
 */

import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { DatePicker } from '../ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import { cn } from '../../lib/utils';
import { getEquipmentItemRate, getEquipmentItemTotal } from '../../lib/jobFinancials';
import { groupCrewByKind } from '../../lib/crewKinds';
import {
  Trash2,
  GripVertical,
  ChevronsUpDown,
  ChevronDown,
  Pencil,
  Package,
  Tag,
  X,
} from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PHASE_TYPES, defaultRateFor } from '../../lib/phaseRates';



/**
 * The small two-way switch above a row's days field: Day / Wk for equipment,
 * D / H for crew labour. A pill track with the chosen side inverted, sized to
 * sit in the row's 14px label line. `readOnly` draws it without buttons, for
 * the drag overlay.
 */
export const EQUIPMENT_RATE_MODES = [
  { value: 'day', label: 'Day', title: 'Day rate' },
  { value: 'week', label: 'Wk', title: 'Weekly rate' },
];
export const LABOUR_RATE_MODES = [
  { value: 'day', label: 'D', title: 'Day rate' },
  { value: 'hour', label: 'H', title: 'Hourly rate' },
];

export function RateModeToggle({ value, options, onChange, readOnly = false, className }) {
  return (
    <div
      role={readOnly ? undefined : 'radiogroup'}
      aria-label="Rate"
      className={cn('inline-flex items-center rounded-pill bg-mist p-px leading-none', className)}
    >
      {options.map((opt) => {
        const selected = opt.value === value;
        const cls = cn(
          'h-[13px] rounded-pill px-1 text-[10px] leading-[13px] transition-colors duration-200',
          selected ? 'bg-inverse text-inverse-foreground font-medium' : 'text-muted-foreground hover:text-foreground'
        );
        return readOnly ? (
          <span key={opt.value} className={cls}>{opt.label}</span>
        ) : (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            title={opt.title}
            onClick={() => onChange(opt.value)}
            className={cls}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export const DEFAULT_EXPENSE = {
  id: '',
  description: '',
  amount: 0,
  quantity: 1,
};

// Sortable Phase Item Component
export function SortablePhaseItem({ phase, index, onUpdate, onRemove, canRemove, defaultRates, formatCurrency, regionConfig, preserveCustomRate = false }) {
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState(phase.label || '');
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: phase.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleTypeChange = (newType) => {
    const rate = defaultRateFor(newType, defaultRates);
    onUpdate(index, 'type', newType);
    // When creating a job, picking a phase type should apply that type's default
    // rate. When editing, it must not clobber a rate the user has customised.
    if (!preserveCustomRate || !phase.day_rate) {
      onUpdate(index, 'day_rate', rate);
    }
  };

  const handleLabelSave = () => {
    onUpdate(index, 'label', labelValue.trim());
    setIsEditingLabel(false);
  };

  const handleLabelCancel = () => {
    setLabelValue(phase.label || '');
    setIsEditingLabel(false);
  };

  const handleLabelKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLabelSave();
    } else if (e.key === 'Escape') {
      handleLabelCancel();
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg p-4 max-sm:p-3 bg-card mb-3">
      <div className="flex items-start gap-3 sortable-row">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-5 h-5" />
        </button>
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between -mt-0.5">
            <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
              <span className="font-medium">
                {PHASE_TYPES.find(p => p.value === phase.type)?.label || 'Phase'}
              </span>
              
              {/* Label Editor */}
              {isEditingLabel ? (
                <div className="flex items-center gap-1 flex-1 max-w-xs">
                  <Input
                    type="text"
                    value={labelValue}
                    onChange={(e) => setLabelValue(e.target.value)}
                    onKeyDown={handleLabelKeyDown}
                    onBlur={handleLabelSave}
                    placeholder="Add custom label..."
                    className="h-7 text-sm"
                    autoFocus
                  />
                </div>
              ) : (
                <>
                  {phase.label && (
                    <span className="text-muted-foreground">|</span>
                  )}
                  {phase.label && (
                    <span className="text-sm text-muted-foreground truncate">
                      {phase.label}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={() => setIsEditingLabel(true)}
                    title="Add custom label"
                  >
                    <Tag className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                  </Button>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-mono text-muted-foreground">
                {formatCurrency((phase.day_rate || 0) * (phase.estimated_days || 0))}
              </span>
              {canRemove && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onRemove(index)}
                >
                  <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                </Button>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Phase Type</Label>
              <Select value={phase.type} onValueChange={handleTypeChange}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PHASE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Day Rate</Label>
              <Input
                type="number"
                step="0.01"
                value={phase.day_rate || ''}
                onChange={(e) => onUpdate(index, 'day_rate', parseFloat(e.target.value) || 0)}
                className="h-9 no-spinner"
                placeholder="$"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Days</Label>
              <Input
                type="number"
                step="0.5"
                value={phase.estimated_days}
                onChange={(e) => onUpdate(index, 'estimated_days', parseFloat(e.target.value) || 0)}
                className="h-9"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Start Date</Label>
              <DatePicker
                value={phase.start_date || ''}
                onChange={(value) => onUpdate(index, 'start_date', value)}
                placeholder="Select date"
                showIcon={false}
                region={regionConfig.code}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sortable Crew Item Component
export function SortableCrewItem({ crew, index, onUpdate, onRemove, crewMembers, formatCurrency, regionConfig }) {
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [labelValue, setLabelValue] = useState(crew.crew_role || '');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: crew.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleLabelSave = () => {
    onUpdate(index, 'crew_role', labelValue.trim());
    setIsEditingLabel(false);
  };

  const handleLabelCancel = () => {
    setLabelValue(crew.crew_role || '');
    setIsEditingLabel(false);
  };

  const handleLabelKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLabelSave();
    } else if (e.key === 'Escape') {
      handleLabelCancel();
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg p-4 mb-3 bg-background/80">
      <div className="flex items-start gap-3 sortable-row">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-5 h-5" />
        </button>
        <div className="flex-1 space-y-3">
          <div className="flex items-center justify-between -mt-0.5">
            <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
              <span className="font-medium">{crew.crew_name}</span>
              
              {/* Role Label Editor */}
              {isEditingLabel ? (
                <div className="flex items-center gap-1 flex-1 max-w-xs">
                  <Input
                    type="text"
                    value={labelValue}
                    onChange={(e) => setLabelValue(e.target.value)}
                    onKeyDown={handleLabelKeyDown}
                    onBlur={handleLabelSave}
                    placeholder="Add role label..."
                    className="h-7 text-sm"
                    autoFocus
                  />
                </div>
              ) : (
                <>
                  {crew.crew_role && (
                    <span className="text-muted-foreground">|</span>
                  )}
                  {crew.crew_role && (
                    <span className="text-sm text-muted-foreground truncate">
                      {crew.crew_role}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={() => setIsEditingLabel(true)}
                    title="Edit role label"
                  >
                    <Tag className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                  </Button>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-mono text-muted-foreground">
                {formatCurrency(crew.client_rate * crew.days)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onRemove(index)}
              >
                <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Base Rate</Label>
              <Input
                type="number"
                step="0.01"
                value={crew.base_rate || ''}
                onChange={(e) => onUpdate(index, 'base_rate', parseFloat(e.target.value) || 0)}
                className="h-9 no-spinner"
                placeholder="$"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Day Type</Label>
              <Select value={crew.day_type} onValueChange={(value) => onUpdate(index, 'day_type', value)}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="8hr">8 Hour</SelectItem>
                  <SelectItem value="10hr">10 Hour</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Markup %</Label>
              <Input
                type="number"
                step="0.01"
                value={crew.markup_percent || ''}
                onChange={(e) => onUpdate(index, 'markup_percent', parseFloat(e.target.value) || 0)}
                className="h-9 no-spinner"
                placeholder="%"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">{crew.labour_rate_mode === 'hour' ? 'Hours' : 'Days'}</Label>
              <div className="flex items-center gap-1">
                <RateModeToggle
                  className="flex-col"
                  value={crew.labour_rate_mode === 'hour' ? 'hour' : 'day'}
                  options={LABOUR_RATE_MODES}
                  onChange={(mode) => onUpdate(index, 'labour_rate_mode', mode)}
                />
                <Input
                  type="number"
                  step="0.5"
                  value={crew.days}
                  onChange={(e) => onUpdate(index, 'days', parseFloat(e.target.value) || 0)}
                  className="h-9 no-spinner"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Start Date</Label>
              <DatePicker
                value={crew.start_date || ''}
                onChange={(value) => onUpdate(index, 'start_date', value)}
                placeholder="Select date"
                showIcon={false}
                region={regionConfig.code}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sortable Crew Equipment Item Component
export function SortableCrewEquipmentItem({ id, item, index, onUpdate, onRemove, formatCurrency }) {
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [descriptionValue, setDescriptionValue] = useState(item.description || '');

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id || item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  const handleDescriptionSave = () => {
    onUpdate(index, 'description', descriptionValue.trim());
    setIsEditingDescription(false);
  };

  const handleDescriptionCancel = () => {
    setDescriptionValue(item.description || '');
    setIsEditingDescription(false);
  };

  const handleDescriptionKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleDescriptionSave();
    } else if (e.key === 'Escape') {
      handleDescriptionCancel();
    }
  };

  return (
    <div ref={setNodeRef} style={style} className={cn("rounded-lg p-3 bg-mist border border-dashed border-line-strong", isDragging && "invisible")}>
      <div className="flex items-start gap-3 sortable-row">
        <button
          type="button"
          className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 flex-1 min-w-0 mr-4">
              <span className="font-medium">{item.crew_name}</span>
              
              {/* Description Label Editor */}
              {isEditingDescription ? (
                <div className="flex items-center gap-1 flex-1 max-w-xs">
                  <Input
                    type="text"
                    value={descriptionValue}
                    onChange={(e) => setDescriptionValue(e.target.value)}
                    onKeyDown={handleDescriptionKeyDown}
                    onBlur={handleDescriptionSave}
                    placeholder="Equipment description..."
                    className="h-7 text-sm"
                    autoFocus
                  />
                </div>
              ) : (
                <>
                  {item.description && (
                    <span className="text-muted-foreground">|</span>
                  )}
                  {item.description && (
                    <span className="text-sm text-muted-foreground truncate">
                      {item.description}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 flex-shrink-0"
                    onClick={() => setIsEditingDescription(true)}
                    title="Edit description"
                  >
                    <Tag className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
                  </Button>
                </>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="font-mono text-sm font-medium" data-testid={`crew-equipment-amount-${item.id}`}>
                {item.billed_direct ? 'Self Billed' : formatCurrency(item.client_rate * item.days * item.quantity)}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onRemove(index)}
              >
                <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground leading-none flex items-center h-[14px]">Quantity</Label>
              <Input
                type="number"
                min="1"
                value={item.quantity}
                onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value) || 1)}
                className="h-7 text-sm no-spinner border-muted-foreground/40"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-muted-foreground leading-none flex items-center h-[14px]">Days</Label>
              <Input
                type="number"
                step="0.5"
                value={item.days}
                onChange={(e) => onUpdate(index, 'days', parseFloat(e.target.value) || 0)}
                className="h-7 text-sm no-spinner border-muted-foreground/40"
              />
            </div>
            <div className="space-y-1">
              <Label className={`text-[10px] leading-none flex items-center h-[14px] ${item.billed_direct ? 'text-muted-foreground/40' : 'text-muted-foreground'}`}>Markup %</Label>
              <Input
                type="number"
                step="0.01"
                value={item.billed_direct ? '' : (item.markup_percent ?? '')}
                onChange={(e) => onUpdate(index, 'markup_percent', parseFloat(e.target.value) || 0)}
                className="h-7 text-sm no-spinner border-muted-foreground/40"
                placeholder="%"
                disabled={item.billed_direct}
              />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-0.5 h-[14px]">
                <button
                  type="button"
                  onClick={() => onUpdate(index, 'billed_direct', false)}
                  className={`text-[10px] leading-none transition-colors ${!item.billed_direct ? 'font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`crew-equipment-mode-base-${item.id}`}
                >Base Rate</button>
                <span className="text-[10px] text-muted-foreground leading-none px-0.5">/</span>
                <button
                  type="button"
                  onClick={() => onUpdate(index, 'billed_direct', true)}
                  className={`text-[10px] leading-none transition-colors ${item.billed_direct ? 'font-semibold text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`crew-equipment-mode-selfbilled-${item.id}`}
                >Self Billed</button>
              </div>
              {item.billed_direct ? (
                <Input
                  type="text"
                  value={item.base_rate ?? ''}
                  onChange={(e) => onUpdate(index, 'base_rate', e.target.value)}
                  className="h-7 text-sm border-muted-foreground/40"
                  placeholder="TBC"
                  data-testid={`crew-equipment-base-rate-text-${item.id}`}
                />
              ) : (
                <Input
                  type="number"
                  step="0.01"
                  value={item.base_rate || ''}
                  onChange={(e) => onUpdate(index, 'base_rate', parseFloat(e.target.value) || 0)}
                  className="h-7 text-sm no-spinner border-muted-foreground/40"
                  placeholder="$"
                  data-testid={`crew-equipment-base-rate-${item.id}`}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sortable Equipment Item Component
export function SortableEquipmentItem({ id, item, index, onUpdate, onRemove, formatCurrency }) {
  const [showDescription, setShowDescription] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id || item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1, // Hide while dragging (overlay shows instead)
  };

  return (
    <div ref={setNodeRef} style={style} className={cn("p-3 bg-secondary/50 rounded-lg", isDragging && "invisible")}>
      <div className="flex items-start gap-3 sortable-row">
        <button
          type="button"
          className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          {/* Header row - name, rate, and delete button */}
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{item.name}</p>
              <p className="text-xs text-muted-foreground">
                {`${formatCurrency(getEquipmentItemRate(item))}/${item.rate_mode === 'week' ? 'wk' : 'day'}`}
              </p>
              {/* Collapsible Description toggle */}
              {item.description && (
                <button
                  type="button"
                  onClick={() => setShowDescription(!showDescription)}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors mt-0.5"
                >
                  <span>{showDescription ? 'Hide' : 'Show'} description</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${showDescription ? 'rotate-180' : ''}`} />
                </button>
              )}
            </div>
            {/* Desktop: show total and delete inline */}
            <div className="hidden sm:flex items-start gap-2">
              <div className="flex gap-2 -mt-0.5">
                {/* 44px = two digits (17.3px) plus the input's 26px of padding
                    and borders. A third digit scrolls rather than wraps. */}
                <div className="w-[44px]">
                  <Label className="text-[10px] text-muted-foreground leading-none block h-[14px]">Qty</Label>
                  <Input
                    type="number"
                    min="1"
                    value={item.quantity}
                    onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value) || 1)}
                    onFocus={(e) => e.target.select()}
                    className="h-7 text-sm no-spinner mt-0.5"
                  />
                </div>
                {/* Sized to the "Day / Wk" toggle above it, which measures
                    45.6px. The extra 1.4px is slack for a fallback font - the
                    toggle is flex-nowrap, so it would spill rather than wrap. */}
                <div className="w-[47px]">
                  {/* items-start, not items-center: the Qty and Rate labels are
                      leading-none blocks whose text sits at the top of this
                      14px box. Centring this row instead dropped "Day / Wk"
                      2px below them. */}
                  <div className="flex items-start h-[14px] leading-none">
                    <RateModeToggle
                      value={item.rate_mode === 'week' ? 'week' : 'day'}
                      options={EQUIPMENT_RATE_MODES}
                      onChange={(mode) => onUpdate(index, 'rate_mode', mode)}
                    />
                  </div>
                  <Input
                    type="number"
                    min="1"
                    value={item.scheduled_days}
                    onChange={(e) => onUpdate(index, 'scheduled_days', parseInt(e.target.value) || 1)}
                    onFocus={(e) => e.target.select()}
                    className="h-7 text-sm no-spinner mt-0.5"
                  />
                </div>
                <div className="w-16">
                  <Label className="text-[10px] text-muted-foreground leading-none block h-[14px]">Rate</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={item.rate_override !== null && item.rate_override !== undefined
                      ? item.rate_override
                      : (item.rate_mode === 'week' ? (item.weekly_rate || '') : (item.daily_rate || ''))}
                    onChange={(e) => {
                      const val = parseFloat(e.target.value);
                      onUpdate(index, 'rate_override', isNaN(val) ? null : val);
                    }}
                    onFocus={(e) => e.target.select()}
                    className="h-7 text-sm no-spinner mt-0.5"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Fixed floor so the line total cannot shift the Qty/Days/Rate
                    inputs out of column. The whole right-hand block is sized to
                    its content and anchored right, so without this a longer
                    total pushes the inputs left and no two rows line up.
                    84px fits $99,999.00 - measured, not guessed. A single
                    equipment line in six figures grows the box instead, which
                    beats overlapping the Rate input. Do not lower this to fit
                    only a short total like $50.00 (51px): the common ones are
                    wider, so they would all push and the columns would drift
                    apart again. */}
                <span className="font-mono text-sm font-medium min-w-[84px] text-right">
                  {formatCurrency(
                    (item.rate_override !== null && item.rate_override !== undefined
                      ? item.rate_override
                      : (item.rate_mode === 'week' ? (item.weekly_rate || 0) : (item.daily_rate || 0)))
                    * item.scheduled_days * item.quantity
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => onRemove(index)}
                >
                  <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                </Button>
              </div>
            </div>
            {/* Mobile: just delete button */}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 sm:hidden -mt-1"
              onClick={() => onRemove(index)}
            >
              <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
            </Button>
          </div>
          
          {/* Mobile: Qty/Days/Rate/Total row underneath. Qty and Days are
              sized to their content (Days to its Day/Wk switch), the rate
              takes what is left, and every label sits in the same 16px line
              so the four inputs line up. Inputs are 36px for a thumb. */}
          <div className="grid grid-cols-[3rem_3.5rem_minmax(0,1fr)_auto] gap-2 mt-3 sm:hidden items-end">
            <div>
              <Label className="block h-4 text-[11px] leading-4 text-muted-foreground">Qty</Label>
              <Input
                type="number"
                min="1"
                value={item.quantity}
                onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value) || 1)}
                onFocus={(e) => e.target.select()}
                className="h-9 px-3 text-sm no-spinner mt-1"
              />
            </div>
            <div>
              <div className="flex items-center h-4">
                <RateModeToggle
                  value={item.rate_mode === 'week' ? 'week' : 'day'}
                  options={EQUIPMENT_RATE_MODES}
                  onChange={(mode) => onUpdate(index, 'rate_mode', mode)}
                />
              </div>
              <Input
                type="number"
                min="1"
                value={item.scheduled_days}
                onChange={(e) => onUpdate(index, 'scheduled_days', parseInt(e.target.value) || 1)}
                onFocus={(e) => e.target.select()}
                className="h-9 px-3 text-sm no-spinner mt-1"
              />
            </div>
            <div className="min-w-0">
              <Label className="block h-4 text-[11px] leading-4 text-muted-foreground">Rate</Label>
              <Input
                type="number"
                step="0.01"
                value={item.rate_override !== null && item.rate_override !== undefined
                  ? item.rate_override
                  : (item.rate_mode === 'week' ? (item.weekly_rate || '') : (item.daily_rate || ''))}
                onChange={(e) => {
                  const val = parseFloat(e.target.value);
                  onUpdate(index, 'rate_override', isNaN(val) ? null : val);
                }}
                onFocus={(e) => e.target.select()}
                className="h-9 px-3 text-sm no-spinner mt-1"
              />
            </div>
            <div className="text-right">
              <Label className="block h-4 text-[11px] leading-4 text-muted-foreground">Total</Label>
              <div className="h-9 flex items-center justify-end font-mono text-sm font-medium mt-1">
                {formatCurrency(
                  (item.rate_override !== null && item.rate_override !== undefined
                    ? item.rate_override
                    : (item.rate_mode === 'week' ? (item.weekly_rate || 0) : (item.daily_rate || 0)))
                  * item.scheduled_days * item.quantity
                )}
              </div>
            </div>
          </div>
          
          {/* Expanded Description */}
          {item.description && showDescription && (
            <p className="text-sm text-muted-foreground mt-2 pl-4 border-l-2 border-border whitespace-pre-line">
              {item.description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// Sortable Expense Item Component
// `canLinkCrew` shows the crew-member link (for crew POs). Crew is a plan
// feature, gated the same way as the form's crew sections.
export function SortableExpenseItem({ expense, index, onUpdate, onRemove, formatCurrency, crewMembers, canLinkCrew = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: expense.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const linkedCrew = crewMembers?.find(c => c.id === expense.linked_crew_id);

  return (
    <div ref={setNodeRef} style={style} className="p-3 bg-secondary/50 rounded-lg space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Input
          value={expense.description}
          onChange={(e) => onUpdate(index, 'description', e.target.value)}
          placeholder="Description"
          className="h-8 flex-1"
        />
        <Input
          type="number"
          step="0.01"
          min="0"
          value={expense.amount || ''}
          onChange={(e) => onUpdate(index, 'amount', parseFloat(e.target.value) || 0)}
          placeholder="Amount"
          className="h-8 w-24 no-spinner"
        />
        <span className="text-muted-foreground text-sm">×</span>
        <Input
          type="number"
          min="1"
          value={expense.quantity}
          onChange={(e) => onUpdate(index, 'quantity', parseInt(e.target.value) || 1)}
          className="h-8 w-16 no-spinner"
        />
        <div className="font-mono text-sm min-w-[80px] text-right">
          {formatCurrency((expense.amount || 0) * (expense.quantity || 1))}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => onRemove(index)}
        >
          <Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" />
        </Button>
      </div>
      {/* Crew member linking, for crew POs */}
      {canLinkCrew && (
      <div className="flex items-center gap-2 pl-6">
        <Label className="text-xs text-muted-foreground">Link to crew member (for PO):</Label>
        <Select 
          value={expense.linked_crew_id || 'none'} 
          onValueChange={(value) => onUpdate(index, 'linked_crew_id', value === 'none' ? null : value)}
        >
          <SelectTrigger className="h-7 w-48 text-xs">
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            {crewMembers?.filter(c => c.status === 'active').map((crew) => (
              <SelectItem key={crew.id} value={crew.id}>
                {crew.name} {crew.role && `(${crew.role})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {linkedCrew && (
          <span className="text-xs text-muted-foreground">→ Will appear on {linkedCrew.name}'s PO</span>
        )}
      </div>
      )}
    </div>
  );
}

// Sortable Package Item Component
export function SortablePackageItem({ id, pkg, equipment, onEdit, onRemove, calculatePackageTotal, formatCurrency }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: id || pkg.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1, // Hide while dragging (overlay shows instead)
  };

  const { subtotal, discount, total } = calculatePackageTotal(pkg);
  const itemsInPackage = equipment.filter(item => pkg.equipment_ids.includes(item.inventory_id));

  // Get discount label based on type
  const getDiscountLabel = () => {
    if (pkg.discount_type === 'percentage') {
      return `${pkg.discount_value}% off`;
    } else if (pkg.discount_type === 'budget') {
      return `Budget: ${formatCurrency(pkg.discount_value)}`;
    } else {
      return `${formatCurrency(pkg.discount_value)} off`;
    }
  };

  const hasDiscount = pkg.discount_value > 0;
  const isBudgetOverSubtotal = pkg.discount_type === 'budget' && pkg.discount_value > subtotal;

  return (
    <div ref={setNodeRef} style={style} className={cn("p-3 bg-secondary/50 rounded-lg", isDragging && "invisible")}>
      <div className="flex items-start gap-3 sortable-row">
        <button
          type="button"
          className="mt-0.5 cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-primary" />
              <span className="font-medium">{pkg.name}</span>
              {hasDiscount && (
                <span className="text-xs text-muted-foreground px-2 py-0.5 bg-primary/10 rounded">
                  {getDiscountLabel()}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onEdit(pkg)}
              >
                <Pencil className="w-4 h-4 text-muted-foreground hover:text-foreground" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => onRemove(pkg.id)}
              >
                <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
              </Button>
            </div>
          </div>
          
          {/* Budget warning */}
          {isBudgetOverSubtotal && (
            <div className="mb-2 p-2 bg-ochre-soft rounded-small text-sm text-ochre">
              ⚠️ Budget ({formatCurrency(pkg.discount_value)}) exceeds subtotal ({formatCurrency(subtotal)})
            </div>
          )}
          
          {/* Items in package */}
          <div className="space-y-1 mb-2 pl-1">
            {itemsInPackage.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{item.name}</span>
                <span className="font-mono text-muted-foreground">
                  {formatCurrency(getEquipmentItemTotal(item))}
                </span>
              </div>
            ))}
          </div>
          
          {/* Package totals */}
          <div className="border-t pt-2 space-y-1">
            {hasDiscount ? (
              <>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-mono line-through text-muted-foreground">{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm text-primary">
                  <span>Package Discount</span>
                  <span className="font-mono">-{formatCurrency(discount)}</span>
                </div>
              </>
            ) : null}
            <div className="flex justify-between font-medium">
              <span>Package Total</span>
              <span className="font-mono">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Sortable Divider Line Component
export function SortableDividerItem({ id, divider, onUpdate, onRemove }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const measureRef = useRef(null);
  const [inputWidth, setInputWidth] = useState(120);

  useEffect(() => {
    if (measureRef.current) {
      setInputWidth(measureRef.current.offsetWidth + 12);
    }
  }, [divider.label]);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn("flex items-center gap-2 py-1", isDragging && "invisible")}
      data-testid={`equipment-divider-${divider.id}`}
    >
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="h-px flex-1 bg-border" />
      <div className="relative flex-shrink-0">
        <span
          ref={measureRef}
          aria-hidden="true"
          className="invisible absolute left-0 top-0 whitespace-pre px-3 text-[15px] font-medium"
        >
          {divider.label || 'Section label'}
        </span>
        <Input
          value={divider.label || ''}
          onChange={(e) => onUpdate(divider.id, e.target.value)}
          placeholder="Section label"
          className="h-8 text-center text-[15px] font-medium border-0 shadow-none px-3"
          style={{ width: `${inputWidth}px` }}
          data-testid={`equipment-divider-input-${divider.id}`}
        />
      </div>
      <div className="h-px flex-1 bg-border" />
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={() => onRemove(divider.id)}
        data-testid={`equipment-divider-remove-${divider.id}`}
      >
        <X className="w-4 h-4 text-muted-foreground" />
      </Button>
    </div>
  );
}


// Equipment Multi-Select Component (stays open for quick multiple selection)
export function EquipmentMultiSelect({ equipment, selectedIds, onSelect, formatCurrency }) {
  const [open, setOpen] = useState(false);
  
  const availableEquipment = equipment.filter(e => e.is_active && !selectedIds.includes(e.id));
  
  const handleSelect = (itemId) => {
    onSelect(itemId);
    // Keep the popover open for quick multiple selections
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          data-testid="add-equipment-select"
        >
          <span className="text-muted-foreground truncate">
            {availableEquipment.length > 0 
              ? 'Select'
              : 'All added'}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search equipment..." />
          <CommandList>
            <CommandEmpty>No equipment found.</CommandEmpty>
            <CommandGroup>
              {availableEquipment.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.name}
                  onSelect={() => handleSelect(item.id)}
                  className="cursor-pointer"
                >
                  <span className="flex-1">{item.name}</span>
                  <span className="text-muted-foreground text-sm">
                    {formatCurrency(item.daily_rate)}/day
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
        {availableEquipment.length > 0 && (
          <div className="border-t p-2">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-muted-foreground"
              onClick={() => setOpen(false)}
            >
              Done
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The crew book picker behind "Add Crew" and the crew equipment "Select".
 *
 * Both used to be their own copy of this component. What differs between them
 * is an argument: which kinds are offered, whether the list stays open for
 * several picks, and what each row shows on the right.
 */
export function CrewPickerSelect({
  crewMembers,
  onSelect,
  kinds = ['crew', 'supplier'],
  stayOpen = false,
  triggerLabel,
  emptyLabel,
  renderDetail,
  isSuggested,
  suggestedLabel = 'Suggested',
}) {
  const [open, setOpen] = useState(false);

  // Optionally lift some members into a first group, e.g. those whose kit
  // list has the item being sourced. They are not repeated below it.
  const all = groupCrewByKind(crewMembers, kinds);
  const suggested = isSuggested ? all.flatMap((g) => g.members.filter(isSuggested)) : [];
  const groups = [
    ...(suggested.length ? [{ kind: 'suggested', label: suggestedLabel, members: suggested }] : []),
    ...all
      .map((g) => ({ ...g, members: g.members.filter((m) => !suggested.includes(m)) }))
      .filter((g) => g.members.length > 0),
  ];
  const showHeadings = groups.length > 1;
  const searchLabel = kinds.includes('supplier') ? 'Search crew and suppliers...' : 'Search crew...';

  const handleSelect = (crewId) => {
    onSelect(crewId);
    if (!stayOpen) setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          <span className="text-muted-foreground truncate">
            {groups.length > 0 ? triggerLabel : emptyLabel}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchLabel} />
          <CommandList>
            <CommandEmpty>Nobody found.</CommandEmpty>
            {groups.map((group) => (
              <CommandGroup key={group.kind} heading={showHeadings ? group.label : undefined}>
                {group.members.map((crew) => (
                  <CommandItem
                    key={crew.id}
                    value={`${crew.name} ${crew.role || ''} ${crew.id}`}
                    onSelect={() => handleSelect(crew.id)}
                    className="cursor-pointer"
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <span>{crew.name}</span>
                        {crew.role && <span className="text-xs text-muted-foreground ml-2">({crew.role})</span>}
                      </div>
                      {renderDetail && <span className="text-xs text-muted-foreground">{renderDetail(crew)}</span>}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// Labour is people, so suppliers are not offered. Stays open for several picks.
export function CrewLabourSelect({ crewMembers, onSelect, formatCurrency }) {
  return (
    <CrewPickerSelect
      crewMembers={crewMembers}
      onSelect={onSelect}
      kinds={['crew']}
      stayOpen
      triggerLabel="Add Crew"
      emptyLabel="No active crew members"
      renderDetail={formatCurrency
        ? (crew) => `${formatCurrency(Number(crew.default_day_rate) || 0)}/${crew.day_type}`
        : undefined}
    />
  );
}

// Equipment can come from a crew member or a supplier.
export function CrewEquipmentSelect({ crewMembers, onSelect }) {
  return (
    <CrewPickerSelect
      crewMembers={crewMembers}
      onSelect={onSelect}
      triggerLabel="Select"
      emptyLabel="No crew or suppliers"
    />
  );
}
